/**
 * Thin fetch wrapper for the A5Core API.
 * Base URL is configurable via VITE_API_URL env var, defaults to /api.
 *
 * AUTHENTICATION MODEL:
 * - The JWT lives in an httpOnly cookie set by the server at login. It is NOT
 *   readable by JavaScript and is never persisted to localStorage — an XSS
 *   cannot steal a persistent token. All requests send it automatically
 *   (same-origin, plus credentials: 'include').
 * - After login we ALSO keep the token returned in the response body in memory
 *   for the lifetime of the page (AiBridge calls and explicit Bearer-header
 *   fetches use it). On reload it is gone; the cookie re-authenticates.
 * - localStorage holds only a non-sensitive session hint (role + expiry) used
 *   for fast route gating. The API remains the authority — a 401 clears it.
 */

export const BASE_URL: string = import.meta.env.VITE_API_URL ?? '/api';

const SESSION_KEY = 'a5core_session';
const WORKSPACE_ID_KEY = 'a5core_workspace_id';

export class ApiError extends Error {
  status: number;
  statusText: string;
  body: string;

  constructor(status: number, statusText: string, body: string) {
    super(`API Error ${status}: ${statusText}`);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.body = body;
  }
}

// Session state

interface SessionHint {
  role: string;
  expiresAt: string;
}

/** In-memory token from the login response — gone on page reload (by design). */
let memoryToken: string | null = null;

function readSessionHint(): SessionHint | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const hint = JSON.parse(raw) as SessionHint;
    if (!hint || typeof hint.expiresAt !== 'string') return null;
    return hint;
  } catch {
    return null;
  }
}

function writeSessionHint(hint: SessionHint): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(hint));
}

function clearSessionHint(): void {
  localStorage.removeItem(SESSION_KEY);
}

// Token management (compatibility surface — memory only, never persisted)

export function getToken(): string | null {
  return memoryToken;
}

export function setToken(token: string): void {
  memoryToken = token;
}

export function clearToken(): void {
  memoryToken = null;
  clearSessionHint();
}

/** Current user's role from the session hint ('Admin', 'Accountant', 'Viewer', …), or null. */
export function getRole(): string | null {
  return readSessionHint()?.role ?? null;
}

export function isAdmin(): boolean {
  return getRole()?.toLowerCase() === 'admin';
}

export function isAuthenticated(): boolean {
  const hint = readSessionHint();
  if (!hint) return false;
  // Local expiry check only — a stale hint gets corrected by the next API 401.
  return Date.now() < new Date(hint.expiresAt).getTime();
}

// Auth API

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  expiresAt: string;
  role: string;
}

export async function login(request: LoginRequest): Promise<LoginResponse> {
  const response = await fetch(`${BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new ApiError(response.status, response.statusText, body);
  }

  const data = await response.json() as LoginResponse;
  // Keep the token in memory only; the httpOnly cookie is the persistent credential.
  setToken(data.token);
  writeSessionHint({ role: data.role, expiresAt: data.expiresAt });
  return data;
}

export function logout(): void {
  // Best-effort server-side cookie clear (fire-and-forget).
  fetch(`${BASE_URL}/auth/logout`, { method: 'POST', credentials: 'include', keepalive: true })
    .catch(() => { /* the local clear below is authoritative for the UI */ });
  clearToken();
}

// Core request helper

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const headers: Record<string, string> = {
    'Accept': 'application/json',
    ...(options.headers as Record<string, string> ?? {}),
  };

  // Bearer header when we hold an in-memory token (post-login, same page);
  // otherwise the httpOnly cookie authenticates (set for every request below).
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Attach workspace ID if available (skip for workspace management endpoints)
  const workspaceId = localStorage.getItem(WORKSPACE_ID_KEY);
  if (workspaceId && !path.startsWith('/workspaces') && !path.startsWith('/auth')) {
    headers['X-Workspace-Id'] = workspaceId;
  }

  if (options.body && typeof options.body === 'string') {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, { ...options, headers, credentials: 'include' });

  if (!response.ok) {
    if (response.status === 401) {
      clearToken();
    }
    const body = await response.text().catch(() => '');
    throw new ApiError(response.status, response.statusText, body);
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string, params?: Record<string, string | number | boolean | undefined>) => {
    const searchParams = new URLSearchParams();
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null) {
          searchParams.set(key, String(value));
        }
      }
    }
    const query = searchParams.toString();
    return request<T>(query ? `${path}?${query}` : path);
  },

  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),

  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),

  delete: <T>(path: string) =>
    request<T>(path, { method: 'DELETE' }),

  upload: <T>(path: string, formData: FormData) =>
    request<T>(path, { method: 'POST', body: formData }),
};
